import type { ReactNode } from 'react';
import { CheckIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { PracticeFieldDef } from './fieldDefinitions';
import { DETAIL_LABEL_CLASS, DETAIL_SPAN_CLASS } from './detailFieldLayout';
import { ToneDropdownField } from './ToneDropdownField';

export type DetailFieldState = 'idle' | 'changed' | 'saving' | 'saved' | 'failed';

// 'comfortable' (the default everywhere except the create page) keeps the
// Details tab's existing card/field sizing untouched. 'compact' is the
// create page's opt-in, tighter sizing - see PracticeCreatePage.
export type DetailDensity = 'comfortable' | 'compact';

const BORDER_BY_STATE: Record<DetailFieldState, string> = {
    idle: 'border-gray-200 bg-white',
    changed: 'border-gray-400 ring-2 ring-[#FFF6DC] bg-white',
    saving: 'border-gray-200 bg-gray-50',
    saved: 'border-gray-200 bg-white',
    failed: 'border-red-300 bg-red-50'
};

interface DetailFieldProps {
    field: PracticeFieldDef;
    value: string;
    state: DetailFieldState;
    errorMessage?: string;
    options: string[];
    disabled: boolean;
    density?: DetailDensity;
    /**
     * Marks required fields with an asterisk after the label. Off by
     * default - the Details tab edits an existing record where required-ness
     * only matters if you blank a field, so it stays unmarked there; the
     * create page turns it on so the three mandatory fields are visible up
     * front.
     */
    showRequiredMarker?: boolean;
    /**
     * Rendered below the field's control box (and any error/helper text) -
     * currently the Details tab's "Create Bill.com customer" / "Request
     * Spark account" buttons for the billId/sparkAcct fields, shown only
     * while that field is still empty (see DetailFieldGrid's
     * fieldAccessories and DetailsTab). The field itself always renders its
     * normal input box alongside this - the user can type a value directly
     * or use the action; either way removes the accessory on the next
     * render, since its presence is keyed off the live value by the caller,
     * not by this component. Undefined for every other field.
     */
    accessory?: ReactNode;
    /**
     * Rendered inside the field's control box, to the right of the value and
     * before the select chevron/status icon (see D2Field's `adornment` slot
     * in the design source) - currently only the Practice card's phone field,
     * as a `tel:` CALL link shown while a value exists (see InfoSection).
     * Unlike `accessory` above (below the box, shown while a field is
     * empty), this renders inline and only while the field is NOT empty.
     */
    adornment?: ReactNode;
    /**
     * Dims the whole field while true - riskNote's only use today (see
     * fieldDefinitions.ts's `deemphasizeWhenFieldEquals` and
     * DetailFieldGrid, which computes this). Purely visual: the field stays
     * fully interactive and its value untouched, so toggling this off and on
     * never loses anything already typed.
     */
    deemphasized?: boolean;
    onChange: (value: string) => void;
}

function FieldStatusIcon({ state }: { state: DetailFieldState }) {
    if (state === 'saving') {
        return (
            <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin shrink-0" />
        );
    }
    if (state === 'saved') {
        return <CheckIcon className="h-4 w-4 text-green-600 shrink-0" strokeWidth={2.5} />;
    }
    if (state === 'failed') {
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500 shrink-0" />;
    }
    return null;
}

function FieldInput({
    field,
    value,
    disabled,
    onChange,
    options,
    density
}: Pick<DetailFieldProps, 'field' | 'value' | 'disabled' | 'onChange' | 'options'> & {
    density: DetailDensity;
}) {
    const inputClass =
        'flex-1 min-w-0 bg-transparent outline-none text-[13px] text-gray-800 placeholder-gray-400 disabled:cursor-not-allowed';

    if (field.type === 'longtext') {
        return (
            <textarea
                id={`practice-field-${field.key}`}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                rows={density === 'compact' ? 2 : 3}
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                className={`${inputClass} resize-none leading-relaxed`}
            />
        );
    }

    if (field.type === 'select') {
        return (
            <select
                id={`practice-field-${field.key}`}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className={`${inputClass} appearance-none`}
            >
                {/* A required select never saves a blank value (see requiredFieldError), so its placeholder is dropped rather than offering a choice that always fails on save. */}
                {!field.required && <option value="">Not set</option>}
                {options.map((option) => (
                    <option key={option} value={option}>
                        {field.optionLabels?.[option] ?? option}
                    </option>
                ))}
            </select>
        );
    }

    const htmlType = field.type === 'number' ? 'number' : field.type === 'tel' ? 'tel' : 'text';
    return (
        <input
            id={`practice-field-${field.key}`}
            type={htmlType}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} ${field.mono ? 'font-mono text-[13px]' : ''}`}
        />
    );
}

export function DetailField({
    field,
    value,
    state,
    errorMessage,
    options,
    disabled,
    density = 'comfortable',
    showRequiredMarker = false,
    accessory,
    adornment,
    deemphasized = false,
    onChange
}: DetailFieldProps) {
    const isLong = field.type === 'longtext';
    const isToneDropdown = field.kind === 'toneDropdown' && field.toneOptions;
    const isCompact = density === 'compact';
    // Additive, independent of `state` - a field can be both freshly changed
    // AND still empty (the user cleared it). Never shown for a required
    // field: emptying one of those is a save-blocking error on its own (see
    // requiredFieldError), not a "consider filling this in" nudge.
    const isEmpty = value.trim() === '';
    const showEmptyDot = !field.required && isEmpty;

    return (
        <div
            className={`flex flex-col ${isCompact ? 'gap-1' : 'gap-1.5'} min-w-0 transition-opacity duration-200 ${
                deemphasized ? 'opacity-60' : ''
            } ${DETAIL_SPAN_CLASS[field.span] ?? ''}`}
        >
            <label htmlFor={`practice-field-${field.key}`} className={DETAIL_LABEL_CLASS}>
                {field.label}
                {showRequiredMarker && field.required && (
                    <span className="text-red-500 -ml-1" aria-hidden="true">
                        *
                    </span>
                )}
                {state === 'changed' && (
                    <span className="text-[10px] font-bold text-[#8a6a00] bg-[#FFF6DC] border border-[#FECA42] rounded px-1">
                        EDITED
                    </span>
                )}
                {state === 'failed' && (
                    <span className="text-[10px] font-bold text-red-600">FAILED</span>
                )}
                {showEmptyDot && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FECA42]" title="Empty" />
                )}
            </label>
            {isToneDropdown ? (
                // No bordered input box here - the chip trigger itself carries
                // the "control" affordance (see ToneDropdownField), same as
                // the work-queue priority selector's button-not-nested-in-a-
                // box treatment.
                <div className="flex items-center gap-2">
                    <ToneDropdownField
                        id={`practice-field-${field.key}`}
                        options={field.toneOptions ?? []}
                        value={value}
                        disabled={disabled}
                        onChange={onChange}
                    />
                    <FieldStatusIcon state={state} />
                </div>
            ) : (
                <div
                    className={`relative border rounded-lg flex gap-2 px-3 ${BORDER_BY_STATE[state]} ${
                        isLong
                            ? isCompact
                                ? 'py-2 min-h-[64px] items-start'
                                : 'py-2.5 min-h-[84px] items-start'
                            : isCompact
                              ? 'h-8 items-center'
                              : 'h-9 items-center'
                    }`}
                >
                    <FieldInput
                        field={field}
                        value={value}
                        disabled={disabled}
                        onChange={onChange}
                        options={options}
                        density={density}
                    />
                    {!isEmpty && adornment && (
                        <span className="shrink-0 flex items-center">{adornment}</span>
                    )}
                    {field.type === 'select' && (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                    <FieldStatusIcon state={state} />
                </div>
            )}
            {state === 'failed' && errorMessage && (
                <span className="text-[12px] text-red-600">{errorMessage}</span>
            )}
            {field.helperText && (
                <span className="text-[11px] text-gray-400">{field.helperText}</span>
            )}
            {accessory}
        </div>
    );
}
